import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient as qc } from '@/lib/query';
import { amendedQueryKeys } from '@/models/amendInvalidation';
import type { TestScope } from '@/models/offers';
import { offerKeys } from '@/models/offers';
import {
  InvoiceExtendedFields,
  InvoiceStatus,
  TInvoice,
  TInvoiceOpportunityStatus,
} from '@/types';

const callForJson = async <T,>(endpoint: string, json?: any): Promise<T> => {
  return call<T>(endpoint, { json }).then((res) => res.json());
};

export interface InvoiceStatusCountsResponse {
  all: number;
  issued: number;
  paid: number;
  cancelled: number;
  test: number;
}

export const getInvoiceStatusCounts = async (
  filters?: SearchFilters,
): Promise<InvoiceStatusCountsResponse> => {
  return call<InvoiceStatusCountsResponse>('invoices/status-counts', {
    method: 'GET',
    json: (filters || {}) as any,
  }).then((res) => res.json());
};

export interface SearchFilters {
  invoiceNumber?: string;
  clientName?: string;
  clientEmail?: string;
  amount?: number;
  currency?: string;
  status?: InvoiceStatus;
  dateFrom?: Date;
  dateTo?: Date;
  source?: 'offer' | 'manual' | 'all';
  scope?: TestScope;
  reported?: boolean;
  collection?: 'none' | 'partly' | 'fully' | 'overpaid';
}

export interface InvoiceListParams {
  page?: number;
  limit?: number;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  status?: InvoiceStatus | 'all';
  filters?: SearchFilters;
  source?: 'offer' | 'manual' | 'all';
  scope?: TestScope;
}

export interface InvoiceListResponse {
  invoices: TInvoice[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

export interface TInstallmentReissue {
  offerId: number;
  planItemId: number;
  seq: number;
  available: boolean;
  reason: string | null;
  /** Installment 1 is issued again only with Issue new proforma on the offer. */
  viaNewProforma?: boolean;
  currentInvoice: { id: number; invoiceNumber: string; status: string } | null;
}

export interface InvoiceDetails extends TInvoice, InvoiceExtendedFields {
  /** Every client payment report, removed ones included, oldest first. */
  paymentReports?: TPaymentReport[];
  reissue?: TInstallmentReissue | null;
  /** The invoice this proforma was settled by, once Settle & issue has run. */
  finalInvoice?: {
    id: number;
    uuid: string;
    number: string;
    issuedAt: string;
    deliveryEmail: string | null;
    pdfStored: boolean;
  } | null;
  /** Settled before final invoices existed: it has none and never gets one. */
  settledBeforeFinalInvoices?: boolean;
  documentVersions?: TDocumentVersions;
  parties?: TDocumentParties | null;
  fx?: TDocumentFx | null;
}

/** The exchange rates a document's amounts were converted with, and the day they are of. */
export interface TDocumentFx {
  asOf: string;
  currency?: string;
  rates: {
    from: string;
    to: string;
    rate: number;
    on?: string;
    event?: 'offer rate' | 'currency changed' | 'issue';
    leg?: 'line' | 'share' | 'plan' | 'base' | 'due';
  }[];
}

const FX_LEG: Record<string, string> = {
  plan: 'plan',
  base: 'installment',
  share: 'lines',
  line: 'lines',
  due: 'amount due',
};

/**
 * The rates the document is billed at: what converts its agreed amounts into its own currency,
 * one per currency pair, without the plan's valuation of a mixed order (a step before the
 * document). Falls back to the plan's rates when they are all there is.
 */
export function billedRates(fx: TDocumentFx | null | undefined) {
  const rates = fx?.rates ?? [];
  const billed = rates
    .filter((r) => r.leg !== 'plan')
    .filter(
      (r, i, all) =>
        all.findIndex(
          (x) => x.from === r.from && x.to === r.to && x.rate === r.rate,
        ) === i,
    );
  return billed.length > 0 ? billed : rates;
}

/** "1 EUR = 1.153535 USD (26.10.2026)" per billed rate. */
export function billedRateLines(fx: TDocumentFx | null | undefined): string[] {
  return billedRates(fx).map(
    (r) =>
      `1 ${r.from} = ${Number(r.rate.toFixed(6))} ${r.to} (${(r.on ?? fx?.asOf ?? '').split('-').reverse().join('.')})`,
  );
}

/** One line per conversion: "plan: 1 USD = 0.8669 EUR (offer rate, 26.10.2026)". */
export function fxLines(fx: TDocumentFx | null | undefined): string[] {
  return (fx?.rates ?? []).map((r) => {
    const day = (r.on ?? fx?.asOf ?? '').split('-').reverse().join('.');
    const why = [r.event, day].filter(Boolean).join(', ');
    return `${r.leg ? `${FX_LEG[r.leg] ?? r.leg}: ` : ''}1 ${r.from} = ${Number(
      r.rate.toFixed(6),
    )} ${r.to}${why ? ` (${why})` : ''}`;
  });
}

/** One entry in an invoice's payment history. */
export interface PaymentMovement {
  id: string;
  amount: number;
  collectedAt: string;
  recordedAt: string;
  recordedByEmail: string | null;
  /** Who the money came from; null when not stated. */
  senderName: string | null;
  /** The client payment report this movement settles, when one was linked. */
  paymentReportId: number | null;
}

export type TPaymentReportSource =
  | 'customer_invoice_link'
  | 'customer_offer_page'
  | 'back_office'
  | 'legacy_import';

/** One client payment report: a claim to check, never money received. */
export interface TPaymentReport {
  id: number;
  hash: string | null;
  fileId: number | null;
  fileName: string | null;
  note: string | null;
  source: TPaymentReportSource;
  reportedAt: string | null;
  reportedByEmail: string | null;
  /** The staff member who added it; null for one the client added. */
  reportedByName?: string | null;
  importedAt: string | null;
  removedAt: string | null;
  removedByEmail: string | null;
  removedReason: string | null;
  linkedPaymentIds: string[];
  /** Set on a report removed because its file was replaced: the report that replaced it. */
  replacedBy?: number | null;
}

export interface RecordMovementRequest {
  id: string;
  /** The signed change: a collection is positive, a deduction negative. Never a total. */
  amount: number;
  /** ISO date the money actually moved (bank/wallet), not the entry date. */
  collectedAt: string;
  /**
   * The movement count this screen was showing. The server refuses the write if a
   * colleague's entry has landed since, so an accountant can never record against a
   * collected total they were not actually looking at.
   */
  expectedPaymentSeq: number;
  /** The client payment report this movement settles. */
  paymentReportId?: number | null;
  /** Who the money came from, as the bank or chain shows it. */
  senderName?: string | null;
}

export interface PaymentWriteResponse {
  invoice: TInvoice;
  paymentSeq: number;
  payments: PaymentMovement[];
}

/** The 409 body when someone else moved the invoice first. */
export interface InvoiceChangedBody {
  code: 'INVOICE_CHANGED';
  error: string;
  invoiceId: number;
  status: InvoiceStatus;
  paidAmount: number;
  paymentSeq: number;
  payments: PaymentMovement[];
}

export interface AddPaymentReportRequest {
  id: string;
  /** One per submission: a retry of the same submission returns the same report. */
  requestKey: string;
  transactionHash?: string;
  fileId?: string;
  note?: string;
}

export const keys = {
  all: ['invoices'] as const,
  list: (params?: InvoiceListParams) => [...keys.all, 'list', params] as const,
  details: (id: string) => [...keys.all, 'details', id] as const,
  search: (filters?: SearchFilters) =>
    [...keys.all, 'search', filters] as const,
  statusCounts: (filters?: SearchFilters) =>
    [...keys.all, 'statusCounts', filters] as const,
};

export const invalidateInvoiceQueries = (invoiceId?: string): void => {
  qc.invalidateQueries({ queryKey: keys.all });

  if (invoiceId) {
    qc.invalidateQueries({ queryKey: keys.details(invoiceId) });
  }
};

export const useCreateOpportunity = () => {
  return useMutation(
    {
      mutationFn: (id: number) =>
        call<void>('invoices/create-opportunity', { json: { id } }),
      onSuccess: (_, id) => {
        qc.setQueryData(keys.details(String(id)), (old: InvoiceDetails) => {
          return {
            ...old,
            opportunityStatus: TInvoiceOpportunityStatus.Queued,
          };
        });
        qc.invalidateQueries({ queryKey: keys.all });
      },
    },
    qc,
  );
};

export const useInvoiceList = (params?: InvoiceListParams) => {
  return useQuery(
    {
      queryKey: keys.list(params),
      queryFn: () => {
        // The list reads its filters as top-level query parameters, as the counts do.
        const { filters, ...rest } = params ?? {};
        const requestParams = {
          ...rest,
          ...filters,
          status:
            params?.status && params.status !== 'all'
              ? params.status
              : filters?.status,
        };
        return call<InvoiceListResponse>('invoices/list', {
          method: 'GET',
          json: requestParams as any,
        }).then((res) => res.json());
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
    qc,
  );
};

export const useInvoiceSearch = (filters?: SearchFilters) => {
  return useQuery(
    {
      queryKey: keys.search(filters),
      queryFn: () =>
        call<InvoiceListResponse>('invoices/search', {
          method: 'GET',
          json: { filters } as any,
        }).then((res) => res.json()),
      enabled: !!filters && Object.keys(filters).length > 0,
      staleTime: 2 * 60 * 1000, // 2 minutes
    },
    qc,
  );
};

export const useInvoiceStatusCounts = (filters?: SearchFilters) => {
  return useQuery(
    {
      queryKey: keys.statusCounts(filters),
      queryFn: () => getInvoiceStatusCounts(filters),
      staleTime: 2 * 60 * 1000, // 2 minutes
      refetchOnWindowFocus: false,
    },
    qc,
  );
};

/** Opportunity creation is queued work; these states resolve without another click. */
const OPPORTUNITY_IN_FLIGHT: (string | null | undefined)[] = [
  TInvoiceOpportunityStatus.Queued,
  TInvoiceOpportunityStatus.Processing,
];

export const useInvoiceDetails = (id: string | number) => {
  return useQuery(
    {
      queryKey: keys.details(id.toString()),
      queryFn: () => callForJson<InvoiceDetails>('invoices/details', { id }),
      enabled: !!id,
      placeholderData: keepPreviousData,
      staleTime: 5 * 60 * 1000, // 5 minutes
      // The queue, not the click, decides when the opportunity exists. Without this
      // the button would sit at "Creating..." until the user reloaded the page.
      refetchInterval: (q) =>
        OPPORTUNITY_IN_FLIGHT.includes(
          (q.state.data as InvoiceDetails | undefined)?.opportunityStatus,
        )
          ? 5000
          : false,
    },
    qc,
  );
};

const UNSETTLED = ['queued', 'submission_unknown'];

export const useInvoiceDetailsByOfferId = (id: string | number) => {
  return useQuery(
    {
      queryKey: ['invoices', 'details-by-offer', id],
      queryFn: () =>
        callForJson<{ invoices: InvoiceDetails[] }>(
          'invoices/details-by-offer',
          { id },
        ).then((res) => res.invoices),
      enabled: !!id,
      refetchInterval: (q) =>
        (q.state.data as InvoiceDetails[] | undefined)?.some((i) =>
          UNSETTLED.includes(i.latestEmail?.status ?? ''),
        )
          ? 15000
          : false,
    },
    qc,
  );
};

export const useAddPaymentReport = () => {
  return useMutation(
    {
      mutationFn: async (request: AddPaymentReportRequest) => {
        await callForJson('invoices/add-payment-report', {
          id: Number(request.id),
          requestKey: request.requestKey,
          transactionHash: request.transactionHash || null,
          fileId: request.fileId ? Number(request.fileId) : null,
          note: request.note || null,
        });
        return request.id;
      },
      onSuccess: (invoiceId) => invalidateInvoiceQueries(invoiceId),
    },
    qc,
  );
};

export const useRemovePaymentReport = () => {
  return useMutation(
    {
      mutationFn: async (request: {
        invoiceId: string;
        reportId: number;
        reason: string;
      }) => {
        await callForJson('invoices/remove-payment-report', {
          reportId: request.reportId,
          reason: request.reason,
        });
        return request.invoiceId;
      },
      onSuccess: (invoiceId) => invalidateInvoiceQueries(invoiceId),
      onError: markHandled,
    },
    qc,
  );
};

/** Replaces a wrong file on a payment report; the old report stays on record, removed. */
export const useReplacePaymentReportFile = () => {
  return useMutation(
    {
      mutationFn: async (request: {
        invoiceId: string;
        reportId: number;
        /** Exactly one: the new file, or the new transaction hash. */
        fileId?: number;
        hash?: string;
        reason?: string;
      }) => {
        const out = await callForJson<{ report: { id: number } }>(
          'invoices/replace-payment-report-file',
          {
            reportId: request.reportId,
            ...(request.fileId != null
              ? { fileId: request.fileId }
              : { hash: request.hash }),
            ...(request.reason ? { reason: request.reason } : {}),
          },
        );
        return { invoiceId: request.invoiceId, reportId: out.report.id };
      },
      onSuccess: ({ invoiceId }) => invalidateInvoiceQueries(invoiceId),
      onError: markHandled,
    },
    qc,
  );
};

/** Sets the payment a confirmation is for, afterwards: another collection, or none. */
export const useLinkPaymentReport = () => {
  return useMutation(
    {
      mutationFn: async (request: {
        invoiceId: string;
        /** The payment the confirmation is for, or null for none. */
        paymentId: string | null;
        reportId: number;
      }) => {
        await callForJson('invoices/link-payment-report', {
          invoiceId: Number(request.invoiceId),
          paymentId: request.paymentId,
          reportId: request.reportId,
        });
        return request.invoiceId;
      },
      onSuccess: (invoiceId) => invalidateInvoiceQueries(invoiceId),
      onError: markHandled,
    },
    qc,
  );
};

/**
 * Fetches one invoice from the server and hands back what it now holds.
 *
 * A caller that has just been refused because its view of the invoice went stale cannot act
 * on an invalidation alone: it is still holding the figures that were rejected, and would
 * resubmit them unchanged. This waits for the truth and returns it.
 *
 * It asks the server outright rather than going through `refetchQueries`, which only refetches
 * queries that happen to be cached and mounted and swallows what it cannot do - so a caller
 * reading the cache afterwards could be handed the very staleness it was trying to escape, and
 * told it had been refreshed. This either returns current data or throws.
 */
export const refetchInvoiceDetails = async (
  id: string,
): Promise<InvoiceDetails> => {
  const fresh = await callForJson<InvoiceDetails>('invoices/details', { id });
  qc.setQueryData(keys.details(id), fresh);
  invalidateInvoiceQueries(id);
  return fresh;
};

// Opts a mutation out of the global "Operation Failed" toast (see lib/query.ts), for callers
// that render the failure themselves.
const markHandled = (error: unknown) => {
  if (error && typeof error === 'object') {
    (error as { handled?: boolean }).handled = true;
  }
};

/**
 * Records one movement against an invoice.
 *
 * The payload is the change, not the running total, so two accountants can no longer
 * overwrite each other by both stating what they believe the invoice holds. What stops the
 * second of them is `expectedPaymentSeq`: their view stopped being true the moment the first
 * entry landed, and the server says so rather than quietly adding to a figure they never saw.
 *
 * `retry: false` is deliberate. A movement is not idempotent - resending it records the money
 * twice unless the server happens to refuse it - so an automatic retry is exactly the wrong
 * instinct here.
 */
export const useRecordMovement = () => {
  return useMutation(
    {
      retry: false,
      mutationFn: (request: RecordMovementRequest) =>
        callForJson<PaymentWriteResponse>('invoices/record-payment', {
          ...request,
          id: Number(request.id),
        }),
      onSuccess: (res) => {
        qc.setQueryData(
          keys.details(res.invoice.id.toString()),
          (old: InvoiceDetails) => ({
            ...old,
            ...res.invoice,
            payments: res.payments,
            paymentSeq: res.paymentSeq,
          }),
        );
        invalidateInvoiceQueries(res.invoice.id.toString());
      },
      // The modal renders the refusal inline, beside the figures that caused it and the
      // history the accountant has to read. A generic red toast beside that says less.
      onError: markHandled,
    },
    qc,
  );
};

export type TPartyChoice =
  | { kind: 'printed' }
  | { kind: 'payer'; payerId: number };

export type TSettleWarningCode =
  | 'party_not_verified'
  | 'party_differs_from_senders'
  | 'senders_unknown'
  | 'vat_differs'
  | 'collected_differs'
  | 'no_delivery_email';

/** The final invoice exactly as Settle & issue would create it now. */
export interface TSettlePreview {
  /** Signed by the server; Issue is sent back with it and refuses when anything moved. */
  intent: string;
  /** Collected differs from due and this preview carries accounting's confirmation. */
  differenceConfirmed: boolean;
  proforma: {
    id: number;
    number: string;
    offerId: number | null;
    accountId: number | null;
  };
  source: { versionId: number | null; version: number };
  paymentSeq: number;
  collected: number;
  due: number;
  currency: string;
  difference: number;
  party: {
    choice: TPartyChoice;
    payerId: number | null;
    name: string;
    billToHtml: string;
    kycValid: boolean;
    isProformaParty: boolean;
  };
  printedPartyOutdated: boolean;
  deliveryEmail: string | null;
  issueDate: string;
  buildId: string;
  warnings: { code: TSettleWarningCode; message: string }[];
  vatWarning: {
    proformaRate: number;
    partyRate: number;
    partyNote: string | null;
  } | null;
  payments: {
    collectedAt: string;
    amount: number;
    senderName: string | null;
  }[];
  /** Payers of the proforma's account; null when this user may not choose one. */
  partyOptions:
    | {
        payerId: number;
        name: string;
        kycValid: boolean;
        email: string | null;
      }[]
    | null;
  html: string;
}

export interface TSettlePreviewRequest {
  id: number;
  party?: TPartyChoice;
  /** Omitted: the party's own address. Empty: issue without one. */
  deliveryEmail?: string | null;
  /** Confirms a difference between collected and due; bound into the signed preview. */
  confirmDifference?: boolean;
}

export const useSettlePreview = () =>
  useMutation(
    {
      retry: false,
      mutationFn: (body: TSettlePreviewRequest) =>
        callForJson<TSettlePreview>('invoices/settle-preview', body),
      onError: markHandled,
    },
    qc,
  );

export interface TIssuedFinalInvoice {
  id: number;
  uuid: string;
  number: string;
  proformaId: number;
  issuedAt: string;
  deliveryEmail: string | null;
  pdfStored: boolean;
}

/**
 * Settle = issue the final invoice. The request key is made once per dialog, so a retry after a
 * lost response returns the same invoice instead of a second one.
 */
export const useSettleIssue = () =>
  useMutation(
    {
      retry: false,
      mutationFn: (body: { id: number; intent: string; requestKey: string }) =>
        callForJson<{ replayed: boolean; finalInvoice: TIssuedFinalInvoice }>(
          'invoices/settle-issue',
          body,
        ),
      onSuccess: (_res, body) => {
        invalidateInvoiceQueries(String(body.id));
      },
      onError: markHandled,
    },
    qc,
  );

export interface TFinalInvoiceDetails {
  fx?: TDocumentFx | null;
  id: number;
  uuid: string;
  number: string;
  kind: 'final_invoice';
  issuedAt: string;
  issuedBy: { id: number; name: string | null };
  deliveryEmail: string | null;
  payerId: number | null;
  partyName: string;
  billToHtml: string;
  vatWarning: TSettlePreview['vatWarning'];
  currency: string;
  dueCurrency: string;
  total: number;
  amountDue: number;
  source: { versionId: number | null; version: number | null };
  proforma: {
    id: number;
    number: string;
    uuid: string;
    status: string;
    state: string;
    offerId: number | null;
    collected: number;
    payments: {
      collectedAt: string;
      amount: number;
      senderName: string | null;
    }[];
    partyName: string;
    payerId: number | null;
  };
  partyDiffersFromProforma: boolean;
  senderWarning: string | null;
  pdf: { state: 'stored' | 'pending' | 'failed'; error: string | null };
  deliveries: {
    sentAt: string;
    recipientEmail: string;
    succeeded: boolean;
    error: string | null;
    sentBy: string | null;
  }[];
  queuedSends: {
    id: number;
    version: number;
    recipientEmail: string;
    status: 'pending' | 'sending';
    createdAt: string;
    lastError: string | null;
  }[];
  /** Why it cannot be emailed from here, or null when it can. */
  sendBlockedReason: string | null;
  parties: TDocumentParties;
}

export const finalInvoiceKeys = {
  details: (id: string) => ['final-invoices', 'details', id] as const,
};

export const useFinalInvoiceDetails = (id: string | number) =>
  useQuery(
    {
      queryKey: finalInvoiceKeys.details(String(id)),
      queryFn: () =>
        callForJson<TFinalInvoiceDetails>('final-invoices/details', {
          id: Number(id),
        }),
      placeholderData: keepPreviousData,
      // A PDF being made or a send waiting for it settles on its own; the page follows it.
      refetchInterval: (q) => {
        const d = q.state.data;
        return d && (d.pdf.state !== 'stored' || d.queuedSends.length > 0)
          ? 5000
          : false;
      },
    },
    qc,
  );

export const useDownloadFinalInvoicePDF = () =>
  useMutation(
    {
      mutationFn: async (id: number) => {
        const response = await call('final-invoices/download-pdf', {
          method: 'GET',
          json: { id } as any,
        });
        if (!response.ok) {
          const reason = response.status === 409 ? await response.text() : '';
          throw new Error(reason || 'Failed to download PDF');
        }
        const disposition = response.headers.get('Content-Disposition');
        const match = disposition?.match(
          /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
        );
        const filename =
          match?.[1]?.replace(/['"]/g, '') || `invoice-${id}.pdf`;
        const url = window.URL.createObjectURL(await response.blob());
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        return { success: true };
      },
    },
    qc,
  );

export const useSendFinalInvoice = () =>
  useMutation(
    {
      mutationFn: (body: { id: number; message?: string }) =>
        callForJson<{
          resent: boolean;
          resentTo?: string;
          queued?: boolean;
          alreadyQueued?: boolean;
          note?: string;
          error?: string;
        }>('final-invoices/send', body),
      onSuccess: (_r, body) => {
        qc.invalidateQueries({
          queryKey: finalInvoiceKeys.details(String(body.id)),
        });
      },
    },
    qc,
  );
export type TDocumentKind = 'proforma' | 'final_invoice';

export interface TGroupFinalInvoice {
  id: number;
  number: string;
  issuedAt: string;
  sent: boolean;
  current: boolean;
}

export interface TGroupProforma {
  id: number;
  number: string;
  status: string;
  state: string;
  partNumber: number | null;
  createdAt: string;
  stateChangedAt: string;
  dueAt: string;
  dueAmount: number;
  dueCurrency: string;
  collected: number;
  documentVersion: number;
  /** The client was sent the latest version. */
  sent: boolean;
  current: boolean;
  finalInvoice: TGroupFinalInvoice | null;
}

export interface TGroupPlan {
  id: number;
  status: string;
  committedAt: string | null;
  updatedAt: string;
  /** The currency the plan's amounts are agreed in. */
  baseCurrency: string;
  count: number;
  items: {
    id: number;
    seq: number;
    /** As stored: before the offer's discount, in the plan's base currency. */
    amount: number | null;
    /** What the customer agreed to pay for it, discount off, in the plan's base currency. */
    agreed: number | null;
    /** How the installment settles; never the currency of its amounts. */
    currency: string;
    proformas: TGroupProforma[];
  }[];
  /** One total, in the plan's base currency. */
  totals: TGroupPlanTotal[];
}

export interface TGroupPlanTotal {
  currency: string;
  due: number;
  collected: number;
  remaining: number;
}

/** Every document related to one document (spec 14.10), returned by one read. */
/** The offer's Monday opportunity, one per offer whatever proforma raised it. */
export interface TGroupMonday {
  itemId: string | null;
  state: 'created' | 'creating' | 'failed' | 'none';
}

export interface TGroupOffer {
  id: number;
  title: string | null;
  accepted: boolean;
  archived: boolean;
  isTest: boolean;
  monday: TGroupMonday;
}

export interface TDocumentGroup {
  anchor: { kind: TDocumentKind; id: number };
  excluded: 'renewal' | null;
  offer: TGroupOffer | null;
  proformas: TGroupProforma[];
  plan: TGroupPlan | null;
  pastPlans: TGroupPlan[];
}

export interface TVersionChange {
  field: string;
  label: string;
  /** The entry of a listing field that changed: a line, a payment detail, a rate. */
  item?: string;
  /** Only follows from another change of the version; folded away by default. */
  effect?: boolean;
  from: string;
  to: string;
}

/** The selected version, rendered from its stored snapshot for the document area. */
export interface TDocumentView {
  kind: TDocumentKind;
  tracked: boolean;
  version: number | null;
  latest: number | null;
  current: boolean;
  createdAt: string | null;
  createdByName: string | null;
  reason: string | null;
  label: string | null;
  note: string;
  html: string | null;
  error: string | null;
  changes: TVersionChange[];
}

export interface TActivityEvent {
  at: string;
  kind:
    | 'state'
    | 'payment'
    | 'report_added'
    | 'report_removed'
    | 'delivery'
    | 'send_queued'
    | 'version'
    | 'final_invoice'
    | 'monday'
    | 'issue';
  title: string;
  detail: string | null;
  actor: string | null;
  tone: 'ok' | 'warn' | 'error' | null;
  key: string;
}

export interface TDocumentParties {
  printedName: string | null;
  /** The payer's details, for reference. */
  details?: {
    clientType: string | null;
    address: string | null;
    country: string | null;
    ids: { label: string; value: string }[];
  } | null;
  payer: {
    id: number;
    name: string;
    clientType: string;
    countryCode: string;
    kycApprovedAt: string | null;
    kycExpiresAt: string | null;
    deleted: boolean;
  } | null;
  account: { id: number; name: string } | null;
  contact: { id: number; name: string; email: string | null } | null;
}

export interface TVersionEntry {
  id: number;
  version: number;
  createdAt: string;
  createdBy: number | null;
  createdByName: string | null;
  reason: string;
  changes: Record<string, { from: unknown; to: unknown }> | null;
  pdfState: 'stored' | 'pending' | 'failed';
  pdfError: string | null;
  label: string | null;
  sentTo: { email: string; sentAt: string }[];
  /** Sent before versions were tracked: no stored file is bound, the exact file is not retained. */
  sentBeforeTracking: {
    email: string;
    sentAt: string;
    version: number | null;
  }[];
}

export interface TDocumentVersions {
  tracked: boolean;
  label: string | null;
  versions: TVersionEntry[];
  detailsOnly: {
    version: number;
    name: string | null;
    reason: string;
    createdAt: string;
    label: string;
  }[];
}

const documentPath = (kind: TDocumentKind) =>
  kind === 'proforma' ? 'invoices' : 'final-invoices';

export const documentKeys = {
  group: (kind: TDocumentKind, id: number) =>
    ['invoices', 'documents', 'group', kind, id] as const,
  view: (kind: TDocumentKind, id: number, version: number | null) =>
    ['invoices', 'documents', 'view', kind, id, version ?? 'current'] as const,
  activity: (kind: TDocumentKind, id: number) =>
    ['invoices', 'documents', 'activity', kind, id] as const,
};

export const useDocumentGroup = (kind: TDocumentKind, id: number) =>
  useQuery(
    {
      queryKey: documentKeys.group(kind, id),
      queryFn: () =>
        callForJson<TDocumentGroup>(`${documentPath(kind)}/document-group`, {
          id,
        }),
      enabled: id > 0,
      placeholderData: keepPreviousData,
    },
    qc,
  );

export const useDocumentView = (
  kind: TDocumentKind,
  id: number,
  version: number | null,
) =>
  useQuery(
    {
      queryKey: documentKeys.view(kind, id, version),
      queryFn: () =>
        callForJson<TDocumentView>(
          `${documentPath(kind)}/document-view`,
          version == null ? { id } : { id, version },
        ),
      enabled: id > 0,
      placeholderData: keepPreviousData,
    },
    qc,
  );

export const useDocumentActivity = (kind: TDocumentKind, id: number) =>
  useQuery(
    {
      queryKey: documentKeys.activity(kind, id),
      queryFn: () =>
        callForJson<{ events: TActivityEvent[] }>(
          `${documentPath(kind)}/activity`,
          { id },
        ).then((r) => r.events),
      enabled: id > 0,
      placeholderData: keepPreviousData,
    },
    qc,
  );

/** After any change on a document page, its group, document area and Activity follow. */
export const invalidateDocumentQueries = (): void => {
  qc.invalidateQueries({ queryKey: ['invoices', 'documents'] });
};

export const useRetryVersionPdf = () =>
  useMutation(
    {
      mutationFn: (body: { id: number; version: number }) =>
        callForJson<unknown>('invoices/version-pdf-retry', body),
      onSuccess: (_r, body) => {
        qc.invalidateQueries({ queryKey: keys.details(String(body.id)) });
      },
    },
    qc,
  );

export const useDownloadInvoicePDF = () => {
  return useMutation(
    {
      mutationFn: async (
        target: string | number | { id: string | number; version?: number },
      ) => {
        const { id: invoiceId, version } =
          typeof target === 'object'
            ? target
            : { id: target, version: undefined };
        const response = await call('invoices/download-pdf', {
          method: 'GET',
          json: (version
            ? { id: invoiceId, version }
            : { id: invoiceId }) as any,
        });

        if (!response.ok) {
          // 409: the stored PDF of that version is still being prepared.
          const reason = response.status === 409 ? await response.text() : '';
          throw new Error(reason || 'Failed to download PDF');
        }

        // Extract filename from Content-Disposition header
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `invoice-${invoiceId}.pdf`; // fallback filename

        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(
            /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
          );
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { success: true };
      },
    },
    qc,
  );
};

// Helper function to get state counts from invoice list
export const getStatusCounts = (invoices: TInvoice[]) => {
  const counts = {
    all: invoices.length,
    [InvoiceStatus.ISSUED]: 0,
    [InvoiceStatus.PAID]: 0,
    [InvoiceStatus.CANCELLED]: 0,
  };

  invoices.forEach((invoice) => {
    if (invoice.status in counts) counts[invoice.status]++;
  });

  return counts;
};
export const useDownloadDocument = () => {
  return useMutation(
    {
      mutationFn: async (documentId: string | number) => {
        const response = await call('document/download', {
          method: 'POST',
          json: { id: parseInt(documentId.toString()) },
        });

        if (!response.ok) {
          throw new Error('Failed to download document');
        }

        // Get content type from response
        const contentType =
          response.headers.get('Content-Type') || 'application/octet-stream';

        // Generate filename based on content type
        let filename = `document-${documentId}`;
        if (contentType.includes('pdf')) {
          filename += '.pdf';
        } else if (contentType.includes('image')) {
          filename += '.jpg';
        } else if (contentType.includes('text')) {
          filename += '.txt';
        } else {
          filename += '.bin';
        }

        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);

        return { success: true };
      },
    },
    qc,
  );
};

export const useResendInvoice = (id: number) => {
  return useMutation(
    {
      mutationFn: () => call('invoices/resend', { json: { id } }),
    },
    qc,
  );
};

export type TSplitPart = {
  id: number;
  invoiceNumber: string | null;
  partNumber: number | null;
  status: string;
  payerId: number | null;
  currency: string;
  dueAmount: number;
  dueAt: string | null;
  paymentMethod: string;
  coverageItemIds: string[];
  /** The installment this invoice was issued for; kept when it is cancelled. */
  splitPlanItemId?: number | null;
  /** What the proforma asks the client to pay, the discount taken off, in its currency. */
  payableAmount?: number;
  /** The rate the plan's base was billed at in this currency; null when it is the base. */
  billedRate?: { rate: number; on: string | null } | null;
};

export const splitKeys = {
  state: (offerId: number) => ['offers', 'split-state', offerId] as const,
};

export type TAmendPayerOption = {
  id: number;
  name: string;
  countryCode: string;
  clientType: string;
  eligible: boolean;
  blockReason: string | null;
  blockText: string | null;
  kycExpiresAt: string | null;
  email: string | null;
};

export type TAmendIssuerOption = {
  id: number;
  name: string;
  countryCode: string;
  priority: number;
  eligible: boolean;
  /**
   * R-1/R-3 are self-dealing rules an override permission can lift. RAIL means
   * the company holds no bank account in the chosen currency, or no wallet for
   * a crypto invoice - a fact about the company that nothing can override.
   */
  blockRule: 'R-1' | 'R-3' | 'RAIL' | null;
  blockText: string | null;
  /** What VAT this company would produce for the buyer under consideration. */
  vatRate?: number;
  /**
   * False where the company levies no VAT at all. A rate of zero says nothing
   * on its own - an exported supply is zero-rated and prints that zero - so
   * the VAT controls are driven by this, never by the rate.
   */
  vatApplicable?: boolean;
  vatNote?: string | null;
};

/** An active client payment report; kept on the invoice through an amendment. */
export type TReportedPayment = {
  id: number;
  source: TPaymentReportSource;
  hash: string | null;
  fileId: number | null;
  fileName: string | null;
  reportedAt: string | null;
  /** The payment it is for, when one is associated. */
  payment?: { amount: number; currency: string; collectedAt: string } | null;
};

export type TAmendOptions = {
  amendable: boolean;
  status: string;
  paidAmount: number;
  reportedPayments: TReportedPayment[];
  mayOverrideSelfDealing: boolean;
  requiresExplicitCurrency: boolean;
  current: {
    payerId: number | null;
    paymentMethod: string;
    currency: string;
    bankCurrency: string | null;
    invoicingCompanyId: number | null;
    vatRate: number | null;
    vatNote: string | null;
    vatOverrideReason: string | null;
    vatOverridden: boolean;
    partNumber: number | null;
  };
  payers: TAmendPayerOption[];
  issuers: TAmendIssuerOption[];
  automaticVat: {
    vatRate: number;
    vatNote: string | null;
    buyerCountry: string;
    buyerType: string;
    buyerVatRegistered: boolean | null;
    companyCountry: string;
    vatApplicable?: boolean;
  } | null;
  planItems: Array<{
    seq: number;
    invoiceId: number | null;
    invoiceNumber: string | null;
    status: string | null;
    amount: number | null;
  }>;
};

export type TAmendRequest = {
  id: number;
  payerId?: number;
  paymentMethod?: string;
  currency?: string;
  invoicingCompanyId?: number;
  overrideSelfDealing?: boolean;
  vat?: {
    /** A fraction, not a percentage: 0.22 is 22%. */
    rate: number;
    reason: string;
  };
  revertVatToAutomatic?: boolean;
  /**
   * Consent to drop a standing manual VAT rate when moving the invoice to an
   * issuing company that levies no VAT. Without it that amendment is refused,
   * so an accountant's rate is never erased as a side effect.
   */
  clearVatOverride?: boolean;
  dueAt?: string;
  reason?: string;
  /** Compute the outcome and roll back, to show real numbers before committing. */
  dryRun?: boolean;
};

export type TAmendedInvoice = {
  id: number;
  invoiceNumber: string;
  partNumber: number | null;
  documentVersion: number;
  subtotalAmount: number;
  vatRate: number;
  vatAutomaticRate: number;
  vatOverridden: boolean;
  vatAmount: number;
  totalAmount: number;
  /** What the client owes once any discount is taken off. */
  payableAmount: number;
  currency: string;
  status: string;
  reportedPayments: TReportedPayment[];
  fxBefore?: TDocumentFx | null;
  fxAfter?: TDocumentFx | null;
  /** Set when a currency change could not start from the KP lines and says what it did instead. */
  fxWarning?: string | null;
};

export type TAmendResult = {
  amended: TAmendedInvoice[];
  inheritedPlanItemIds: number[];
  skipped: Array<{
    seq: number;
    invoiceId: number;
    status: string;
    reason?: string;
  }>;
  /** Every installment of the plan and what the amendment does to it; empty for an order. */
  planImpact?: TPlanImpactRow[];
};

type TMoney = { amount: number; currency: string };

export type TPlanImpactRow = {
  seq: number;
  invoiceId: number | null;
  invoiceNumber: string | null;
  effect: 'this' | 'amended' | 'unchanged' | 'inherits' | 'not issued';
  why: string;
  agreed: TMoney | null;
  before: TMoney | null;
  after: TMoney | null;
  rate: { from: string; to: string; rate: number; on: string | null } | null;
};

/**
 * Eligibility for the amend form. Re-fetched as the accountant changes payer or
 * payment method, because both move which companies may legally issue.
 */
export const useAmendOptions = (
  id: number,
  probe?: {
    payerId?: number;
    paymentMethod?: string;
    currency?: string;
    invoicingCompanyId?: number;
  },
  enabled = true,
) => {
  return useQuery(
    {
      queryKey: ['invoices', 'amend-options', id, probe] as const,
      queryFn: () =>
        callForJson<TAmendOptions>('invoices/amend-options', {
          id,
          ...(probe ?? {}),
        }),
      enabled: enabled && !!id,
      staleTime: 0,
      // Each probe is a new query key. Without this the previous answer is
      // dropped the instant the payer or issuer changes, the form unmounts, and
      // the modal collapses to its header before springing back - a visible
      // flicker on every keystroke-sized change.
      placeholderData: keepPreviousData,
    },
    qc,
  );
};

/** Everything an amendment can move: each invoice it reached, and the offer's schedule. */
const invalidateAmended = (offerId?: number | null) => {
  for (const queryKey of amendedQueryKeys(offerId)) {
    qc.invalidateQueries({ queryKey });
  }
};

export const useAmendInvoice = (offerId?: number) => {
  return useMutation(
    {
      mutationFn: (body: TAmendRequest) =>
        callForJson<TAmendResult>('invoices/amend', body),
      onSuccess: () => invalidateAmended(offerId),
    },
    qc,
  );
};

export interface TBillTo {
  payerId: number;
  clientType: 'Individual' | 'Corporate';
  clientDetails: Record<string, unknown> | null;
  countryCode?: string | null;
  name: string | null;
  email: string | null;
}

export type TBillToRefresh = { dryRun: boolean } & (
  | { refreshed: false; reason: 'unchanged'; billTo: TBillTo | null }
  | {
      refreshed: true;
      version: number;
      before: TBillTo | null;
      after: TBillTo;
      amended: TAmendedInvoice[];
      skipped: Array<{
        seq: number;
        invoiceId: number;
        status: string;
        reason?: string;
      }>;
    }
);

/**
 * What "Update to the payer's current details" would do, computed by the real
 * refresh and rolled back. Keyed under the bill-to history so every amendment
 * or attach that invalidates the history re-asks.
 */
export const useBillToRefreshPreview = (id: number, enabled = true) =>
  useQuery(
    {
      queryKey: ['invoices', 'bill-to-versions', String(id), 'refresh'],
      queryFn: () =>
        callForJson<TBillToRefresh>('invoices/refresh-bill-to', {
          id,
          dryRun: true,
        }),
      enabled: enabled && !!id,
      staleTime: 0,
      retry: false,
    },
    qc,
  );

export const useRefreshBillTo = (offerId?: number | null) =>
  useMutation(
    {
      mutationFn: (id: number) =>
        callForJson<TBillToRefresh>('invoices/refresh-bill-to', { id }),
      onSuccess: () => invalidateAmended(offerId),
    },
    qc,
  );

/** Sending is a deliberate act now: amending never notifies anybody. */
export const useSendInvoice = () => {
  return useMutation(
    {
      mutationFn: (body: { id: number; message?: string }) =>
        callForJson<{
          resent: boolean;
          resentTo?: string;
          resentAt?: string;
          queued?: boolean;
          alreadyQueued?: boolean;
          note?: string;
          error?: string;
        }>('invoices/resend', body),
      onSuccess: (_r, body) => {
        qc.invalidateQueries({ queryKey: keys.details(String(body.id)) });
      },
    },
    qc,
  );
};

export function useCancelInvoice() {
  return useMutation(
    {
      mutationFn: (invoice: { id: number; offerId: number | null }) =>
        callForJson<TInvoice & { noActiveProforma: boolean }>(
          'invoices/cancel',
          {
            id: invoice.id,
          },
        ),
      onSuccess: (_r, invoice) => {
        invalidateInvoiceQueries(String(invoice.id));
        if (invoice.offerId == null) return;
        qc.invalidateQueries({ queryKey: splitKeys.state(invoice.offerId) });
        qc.invalidateQueries({ queryKey: offerKeys.detail(invoice.offerId) });
        qc.invalidateQueries({ queryKey: offerKeys.activity(invoice.offerId) });
        qc.invalidateQueries({ queryKey: offerKeys.lists() });
      },
    },
    qc,
  );
}

export function useInvoiceKycSession() {
  return useMutation(
    {
      mutationFn: (body: { invoiceId: number; payerId?: number }) =>
        call('invoices/kyc-session', { json: body as any }).then(
          (r) =>
            r.json() as Promise<{
              sessionId: number;
              url: string;
              reverify: boolean;
            }>,
        ),
    },
    qc,
  );
}
